// ⋮ (3-dot) menu for the NATIVE (DOM) renderers — table / pivot / cards / feeds
// / calendar / tree / gantt / combo / map / html. The vendored Plotly charts get
// their ⋮ menu from BaseVisual (`_renderChartMenuButton`); this is the React
// analogue so every visual type — not just charts — exposes a menu.
//
// Styled to match BaseVisual's button/dropdown (26px bordered ⋮ glyph; a
// position:fixed dropdown portaled to <body> so a tile's overflow can't clip it
// — same rationale as BaseVisual's floating menu). Items are the honest set for
// a DOM renderer: **Export CSV** (no PNG — these aren't canvases) and
// **Fullscreen**.

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { humanizeColumnLabel, mapVisualData } from './dataUtils'
import type { VisualJsonData, VisualLayout } from './types'
import { CHROME } from './surfaceTheme'

export interface NativeVisualMenuProps {
  jsonData: VisualJsonData
  visualLayout: VisualLayout
  /** Returns the element to make fullscreen (the visual's wrapper). */
  getFullscreenTarget: () => HTMLElement | null
  /** Filename stem for the CSV download (defaults to the visual name/"visual"). */
  fileName?: string
  /**
   * When provided, adds a "View underlying data" item that opens the visual's
   * row-level detail (whole result set — no point filter). Wired by the host
   * (InsightVisual) so the engine stays decoupled from any backend.
   */
  onShowUnderlying?: () => void
  /**
   * When provided, adds a "Filter" item that opens the shared in-visual filter
   * builder (the SAME mechanism the vendored charts' ⋮ "Filter" routes to — see
   * InsightVisual / visualFilter.ts), so a table filters exactly like a chart.
   */
  onOpenFilter?: () => void
  /** True when a filter is in force — the ⋮ button gets an active-filter dot. */
  filterActive?: boolean
}

// Quote a CSV field per RFC 4180 (wrap + double any embedded quotes) when it
// contains a comma, quote, or newline.
function csvField(value: unknown): string {
  if (value == null) return ''
  const s = String(value)
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

// Build a CSV string from the visual's data using its ColumnGroups for column
// order/labels; falls back to the row object's own keys when no groups are
// mapped (mirrors GridTable's resolveColumns fallback).
function buildCsv(jsonData: VisualJsonData, visualLayout: VisualLayout): string {
  const md = mapVisualData(jsonData, visualLayout)
  const seen = new Set<string>()
  const cols: { name: string; label: string }[] = []
  for (const group of Object.values(md.groups)) {
    for (const c of group) {
      if (c.hidden || seen.has(c.name)) continue
      seen.add(c.name)
      cols.push({ name: c.name, label: c.label || c.name })
    }
  }
  if (cols.length === 0 && md.rows.length > 0) {
    for (const k of Object.keys(md.rows[0])) {
      seen.add(k)
      cols.push({ name: k, label: humanizeColumnLabel(k) })
    }
  }
  const header = cols.map((c) => csvField(c.label)).join(',')
  const body = md.rows
    .map((r) => cols.map((c) => csvField(r[c.name])).join(','))
    .join('\r\n')
  return `${header}\r\n${body}`
}

function downloadCsv(text: string, fileName: string): void {
  try {
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    // Revoke on the next tick so the download has claimed the URL.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  } catch (e) {
    console.error('[NativeVisualMenu] CSV export failed:', e)
  }
}

const BTN_STYLE = {
  width: 26, height: 26, border: `1px solid ${CHROME.border}`, background: CHROME.raised,
  borderRadius: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
  justifyContent: 'center', padding: 0, font: '700 18px/1 "Open Sans",Arial,sans-serif',
  color: CHROME.text, boxSizing: 'border-box', flex: '0 0 auto', position: 'relative',
} as const

export function NativeVisualMenu({
  jsonData, visualLayout, getFullscreenTarget, fileName, onShowUnderlying,
  onOpenFilter, filterActive,
}: NativeVisualMenuProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  // The dropdown itself — the dismiss listener runs in the CAPTURE phase at
  // window, before React's delegated handlers, so a press inside the menu has
  // to be recognised HERE. Without it the menu unmounts on mousedown and the
  // item's click never fires (see the listener below).
  const menuRef = useRef<HTMLDivElement | null>(null)

  const close = useCallback(() => setOpen(false), [])

  // Position the dropdown off the button rect (viewport coords) and keep it
  // there while open; close on outside click / Escape / scroll / resize.
  useEffect(() => {
    if (!open) return
    const place = () => {
      const b = btnRef.current
      if (!b) return
      const r = b.getBoundingClientRect()
      setPos({ top: Math.round(r.bottom + 6), right: Math.round(Math.max(8, window.innerWidth - r.right)) })
    }
    place()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (!target) { close(); return }
      // A press on the button (toggle) or ANYWHERE INSIDE the dropdown is not an
      // outside click. Missing the menu here closed it on mousedown, so the
      // item's onClick never ran — "Filter…" opened nothing, and neither did
      // View underlying data / Export CSV / Fullscreen. Same containment rule
      // the vendored chart menu uses (BaseVisual._chartMenuDismiss).
      if (btnRef.current && btnRef.current.contains(target)) return
      if (menuRef.current && menuRef.current.contains(target)) return
      close()
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onDown, true)
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onDown, true)
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, close])

  const onExportCsv = useCallback(() => {
    downloadCsv(buildCsv(jsonData, visualLayout), fileName || 'visual')
    close()
  }, [jsonData, visualLayout, fileName, close])

  const onFullscreen = useCallback(() => {
    close()
    const target = getFullscreenTarget()
    if (!target) return
    try {
      if (document.fullscreenElement) void document.exitFullscreen()
      else void target.requestFullscreen?.()
    } catch (e) {
      console.error('[NativeVisualMenu] fullscreen failed:', e)
    }
  }, [getFullscreenTarget, close])

  const menu = open && pos
    ? createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'fixed', top: pos.top, right: pos.right, minWidth: 180,
            padding: 5, zIndex: 10050, background: CHROME.raised, border: `1px solid ${CHROME.border}`,
            borderRadius: 8, boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
            font: '13px/1.3 "Open Sans",Arial,sans-serif', color: CHROME.text,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {onOpenFilter && (
            <MenuItem
              label={filterActive ? 'Filter (active)…' : 'Filter…'}
              onClick={() => { close(); onOpenFilter() }}
            />
          )}
          {onShowUnderlying && (
            <MenuItem label="View underlying data" onClick={() => { close(); onShowUnderlying() }} />
          )}
          <MenuItem label="Export CSV" onClick={onExportCsv} />
          <MenuItem label="Fullscreen" onClick={onFullscreen} />
        </div>,
        document.body,
      )
    : null

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        title="Visual menu"
        aria-haspopup="menu"
        aria-label="Visual menu"
        style={BTN_STYLE}
        onMouseEnter={(e) => { (e.currentTarget.style.background = CHROME.hover) }}
        onMouseLeave={(e) => { (e.currentTarget.style.background = CHROME.raised) }}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o) }}
      >
        ⋮
        {filterActive && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute', marginTop: -10, marginLeft: 12,
              width: 6, height: 6, borderRadius: '50%', background: CHROME.accent,
            }}
          />
        )}
      </button>
      {menu}
    </>
  )
}

function MenuItem({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
        border: 0, borderRadius: 5, background: 'transparent', color: 'inherit',
        cursor: 'pointer', font: 'inherit', whiteSpace: 'nowrap',
      }}
      onMouseEnter={(e) => { (e.currentTarget.style.background = CHROME.hover) }}
      onMouseLeave={(e) => { (e.currentTarget.style.background = 'transparent') }}
    >
      {label}
    </button>
  )
}

export default NativeVisualMenu
